import React, { useState, useEffect } from 'react';
import { Page } from '../../types/cms';
import cmsAPI from '../../services/api';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Copy, 
  Eye,
  Calendar,
  User,
  Globe,
  FileText
} from 'lucide-react';

interface PageManagerProps {
  onEditPage: (page: Page) => void;
}

const PageManager: React.FC<PageManagerProps> = ({ onEditPage }) => {
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('updatedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    loadPages();
  }, [statusFilter, sortBy, sortOrder]);

  const loadPages = async () => {
    try {
      setLoading(true);
      const response = await cmsAPI.getPages({
        status: statusFilter === 'all' ? undefined : statusFilter as any,
        sortBy: sortBy as any,
        sortOrder,
        search: searchTerm || undefined,
      });
      
      if (response.ok && response.data) {
        setPages(response.data);
      }
    } catch (error) {
      console.error('Error loading pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (pageId: string) => {
    if (!confirm('Are you sure you want to delete this page?')) return;
    
    try {
      const response = await cmsAPI.deletePage(pageId);
      if (response.ok) {
        setPages(pages.filter(p => p.id !== pageId));
      }
    } catch (error) {
      console.error('Error deleting page:', error);
    }
  };

  const handleDuplicate = async (page: Page) => {
    try {
      const duplicateData = {
        title: `${page.title} (Copy)`,
        slug: `${page.slug}-copy`,
        content: page.content,
        excerpt: page.excerpt || '',
        status: 'draft' as const,
        template: page.template,
        tags: page.tags,
        categories: page.categories,
        seo: page.seo,
      };
      
      const response = await cmsAPI.createPage(duplicateData);
      if (response.ok && response.data) {
        setPages([response.data, ...pages]);
      }
    } catch (error) {
      console.error('Error duplicating page:', error);
    }
  };

  const handlePublish = async (pageId: string) => {
    try {
      const response = await cmsAPI.publishPage(pageId);
      if (response.ok && response.data) {
        setPages(pages.map(p => p.id === pageId ? response.data! : p));
      }
    } catch (error) {
      console.error('Error publishing page:', error);
    }
  };

  const filteredPages = pages.filter(page => {
    if (searchTerm) {
      return page.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
             page.content.toLowerCase().includes(searchTerm.toLowerCase());
    }
    return true;
  });

  const getStatusBadge = (status: Page['status']) => {
    const styles = {
      published: 'bg-green-100 text-green-800',
      draft: 'bg-yellow-100 text-yellow-800',
      archived: 'bg-gray-100 text-gray-800',
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pages</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your website pages and content
          </p>
        </div>
        <button
          onClick={() => onEditPage({} as Page)}
          className="mt-4 sm:mt-0 btn-primary inline-flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Page
        </button>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search pages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && loadPages()}
              className="pl-10 input-field"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-field w-auto"
          >
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-');
              setSortBy(field);
              setSortOrder(order as 'asc' | 'desc');
            }}
            className="input-field w-auto"
          >
            <option value="updatedAt-desc">Recently Updated</option>
            <option value="createdAt-desc">Recently Created</option>
            <option value="title-asc">Title A-Z</option>
            <option value="publishedAt-desc">Recently Published</option>
          </select>
        </div>
      </div>

      {/* Pages List */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">Loading pages...</p>
          </div>
        ) : filteredPages.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No pages found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating your first page.
            </p>
            <button
              onClick={() => onEditPage({} as Page)}
              className="mt-4 btn-primary inline-flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Page
            </button>
          </div>
        ) : (
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Page
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Author
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Updated
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPages.map((page) => (
                  <tr key={page.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {page.title}
                          </div>
                          <div className="text-sm text-gray-500">
                            /{page.slug}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(page.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">
                          {page.author?.name || 'Unknown'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-2" />
                        {new Date(page.updatedAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {page.status === 'published' && (
                          <a
                            href={`/${page.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-gray-600"
                            title="View page"
                          >
                            <Eye className="h-4 w-4" />
                          </a>
                        )}
                        <button
                          onClick={() => onEditPage(page)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Edit page"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDuplicate(page)}
                          className="text-gray-400 hover:text-gray-600"
                          title="Duplicate page"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        {page.status === 'draft' && (
                          <button
                            onClick={() => handlePublish(page.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Publish page"
                          >
                            <Globe className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(page.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete page"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PageManager;
